@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  --font-heading: 'Sora', ui-sans-serif, system-ui, sans-serif;
  --font-body: 'Inter', ui-sans-serif, system-ui, sans-serif;
  --font-label: 'Inter', ui-sans-serif, system-ui, sans-serif;

  /* background — branco gelo (#F7FAFA) base + areia (#EAD7B7) detail tints */
  --background-50: 0.985 0.004 195;
  --background-100: 0.968 0.012 120;
  --background-200: 0.935 0.024 100;
  --background-300: 0.895 0.038 85;
  --background-400: 0.825 0.030 90;
  --background-500: 0.705 0.026 92;
  --background-600: 0.585 0.022 95;
  --background-700: 0.472 0.018 100;
  --background-800: 0.372 0.015 110;
  --background-900: 0.282 0.012 120;
  --background-950: 0.202 0.010 130;

  /* primary — azul-petróleo (#006D77) */
  --primary-50: 0.975 0.018 200;
  --primary-100: 0.945 0.032 201;
  --primary-200: 0.885 0.050 203;
  --primary-300: 0.800 0.068 204;
  --primary-400: 0.680 0.082 205;
  --primary-500: 0.488 0.083 206;
  --primary-600: 0.420 0.075 206;
  --primary-700: 0.350 0.063 206;
  --primary-800: 0.285 0.050 206;
  --primary-900: 0.225 0.038 206;
  --primary-950: 0.170 0.028 206;

  /* accent — laranja pôr do sol (#F28C28) */
  --accent-50: 0.975 0.030 70;
  --accent-100: 0.948 0.060 68;
  --accent-200: 0.905 0.095 65;
  --accent-300: 0.855 0.128 62;
  --accent-400: 0.795 0.150 60;
  --accent-500: 0.735 0.161 59;
  --accent-600: 0.655 0.150 56;
  --accent-700: 0.560 0.128 54;
  --accent-800: 0.470 0.105 52;
  --accent-900: 0.385 0.082 50;
  --accent-950: 0.310 0.062 48;

  /* secondary — azul água (#83C5BE) */
  --secondary-50: 0.972 0.016 190;
  --secondary-100: 0.945 0.030 189;
  --secondary-200: 0.900 0.046 188;
  --secondary-300: 0.855 0.058 188;
  --secondary-400: 0.815 0.064 188;
  --secondary-500: 0.777 0.068 188;
  --secondary-600: 0.680 0.062 188;
  --secondary-700: 0.575 0.052 188;
  --secondary-800: 0.475 0.042 188;
  --secondary-900: 0.385 0.032 188;
  --secondary-950: 0.300 0.024 188;

  /* foreground — azul-marinho (#12343B) text scale */
  --foreground-50: 0.975 0.005 210;
  --foreground-100: 0.945 0.009 210;
  --foreground-200: 0.885 0.015 211;
  --foreground-300: 0.800 0.023 211;
  --foreground-400: 0.690 0.031 212;
  --foreground-500: 0.585 0.037 212;
  --foreground-600: 0.510 0.040 213;
  --foreground-700: 0.445 0.042 213;
  --foreground-800: 0.390 0.042 213;
  --foreground-900: 0.340 0.042 213;
  --foreground-950: 0.303 0.041 213;
}

.dark {
  --background-50: 0.175 0.014 206;
  --background-100: 0.220 0.016 206;
  --background-200: 0.275 0.018 206;
  --background-300: 0.340 0.020 206;
  --background-400: 0.440 0.022 206;
  --background-500: 0.560 0.024 206;
  --background-600: 0.660 0.024 206;
  --background-700: 0.760 0.022 206;
  --background-800: 0.850 0.018 206;
  --background-900: 0.920 0.012 206;
  --background-950: 0.970 0.008 206;

  --foreground-50: 0.200 0.012 206;
  --foreground-100: 0.255 0.014 206;
  --foreground-200: 0.340 0.016 206;
  --foreground-300: 0.440 0.016 206;
  --foreground-400: 0.560 0.014 206;
  --foreground-500: 0.660 0.012 206;
  --foreground-600: 0.740 0.011 206;
  --foreground-700: 0.820 0.009 206;
  --foreground-800: 0.885 0.008 206;
  --foreground-900: 0.935 0.006 206;
  --foreground-950: 0.975 0.004 206;
}

@layer base {
  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-body);
    background-color: oklch(var(--background-50));
    color: oklch(var(--foreground-950));
    -webkit-font-smoothing: antialiased;
  }

  h1,
  h2,
  h3,
  h4 {
    font-family: var(--font-heading);
  }
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Mantém o botão do WhatsApp acima da barra de cookies */
[data-whatsapp-cta] {
  transition: transform 0.2s ease, bottom 0.3s ease;
}

body.has-cookie-bar [data-whatsapp-cta] {
  bottom: 8rem;
}

@media (min-width: 768px) {
  body.has-cookie-bar [data-whatsapp-cta] {
    bottom: 11rem;
  }
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.reveal {
  opacity: 0;
  transform: translateY(26px);
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.reveal.revealed {
  opacity: 1;
  transform: none;
}

.reveal-delay-1 {
  transition-delay: 0.1s;
}

.reveal-delay-2 {
  transition-delay: 0.2s;
}

.reveal-delay-3 {
  transition-delay: 0.3s;
}

.reveal-delay-4 {
  transition-delay: 0.4s;
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }

  html {
    scroll-behavior: auto;
  }
}